import { useState } from "react";
import { useTranslation } from "react-i18next";
import authMachineHero from "@/assets/auth-machine-hero.jpg";

/**
 * AuthHeroPanel — D6 Login Split-Screen Industrial-Hero (TES-968, TES-1075)
 *
 * Linkes Deep-Blue-Panel des Login-Split-Screens (Stitch screen-13-login-split).
 * REINE Layout-/Markup-Hülle — keine Auth-Logik, keine Daten. Stats sind dekorativ.
 *
 * TES-1075: Hintergrund = Plasser&Theurer-Bahnbaumaschine (Stopfaggregate,
 * firmeneigenes Material von plassertheurer.com, Owner-autorisiert):
 *   - Poster-JPG als lokales Repo-Asset (Layer 1, immer sichtbar)
 *   - optionaler Video-Loop aus Supabase Storage (public-assets-Bucket) NUR auf
 *     Desktop (>= lg, Panel ist darunter ohnehin hidden) und NUR ohne
 *     prefers-reduced-motion; onError -> Poster-Fallback (Video-Datei wird
 *     separat hochgeladen, UI muss ohne sie funktionieren)
 *   - brand-Gradient als Overlay drüber (Muster Landing-HeroSection):
 *     Text bleibt voll lesbar/WCAG-konform, Light+Dark identisch (brand ist
 *     mode-invariant).
 */

const HERO_VIDEO_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/public-assets/videos/auth-machine-loop.mp4`
  : "";

export function AuthHeroPanel() {
  const { t } = useTranslation();
  const [videoFailed, setVideoFailed] = useState(false);
  // Einmalig beim Mount entschieden (kein Re-Render-Flackern): Video nur auf
  // Desktop-Viewports und ohne reduzierte Bewegung laden — Mobile/Reduced-Motion
  // bekommen ausschließlich das Poster (spart Daten, respektiert a11y).
  const [videoEnabled] = useState(
    () =>
      !!HERO_VIDEO_URL &&
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  return (
    <div
      className="relative hidden overflow-hidden bg-brand lg:flex lg:w-3/5"
      data-testid="auth-hero-panel"
      aria-hidden="true"
    >
      {/* Layer 1: Poster-Standbild (P&T-Bahnbaumaschine) — immer da, auch wenn
          das Video (noch) fehlt oder nicht geladen werden darf. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${authMachineHero})` }}
        data-testid="auth-hero-image"
      />
      {/* Layer 2: Video-Loop (Desktop-only, optional). onError -> unmount,
          darunter bleibt das Poster stehen. */}
      {videoEnabled && !videoFailed && (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={HERO_VIDEO_URL}
          poster={authMachineHero}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setVideoFailed(true)}
          data-testid="auth-hero-video"
        />
      )}
      {/* Layer 3: brand-Gradient-Overlay (Lesbarkeit, gleiches Muster wie
          Landing-HeroSection) + dezente Safety-Yellow-Lichtkante. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 78% 18%, hsl(var(--secondary) / 0.14) 0%, transparent 38%)," +
            "linear-gradient(100deg, hsl(var(--brand) / 0.92) 0%, hsl(var(--brand) / 0.72) 45%, hsl(var(--brand) / 0.45) 100%)",
        }}
      />
      {/* Technisches Dot-Pattern (Overlay) */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      {/* Inhalt unten ausgerichtet, Gradient nach oben auslaufend */}
      <div className="relative z-10 flex h-full w-full flex-col justify-end bg-gradient-to-t from-brand/80 to-transparent p-12 xl:p-20">
        <div className="max-w-xl">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[2px] w-12 bg-secondary" />
            <span className="text-label-caps tracking-widest text-secondary">
              {t("auth.splitScreen.heroLabel")}
            </span>
          </div>
          <h1 className="mb-4 font-display text-h1 leading-tight text-white">
            {t("auth.splitScreen.heroTitle")}
          </h1>
          <p className="max-w-md text-body-lg text-primary-fixed/80">
            {t("auth.splitScreen.heroSubline")}
          </p>
        </div>
      </div>
      {/* Dekorative Daten-Stats unten rechts (font-data-tabular) */}
      <div className="absolute bottom-8 right-8 z-10 flex items-center gap-8 border-l border-white/20 pl-6">
        <div className="flex flex-col">
          <span className="text-[10px] font-bold tracking-tighter text-white/50">
            {t("auth.splitScreen.statLatencyLabel")}
          </span>
          <span className="text-data-tabular text-secondary">14ms</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold tracking-tighter text-white/50">
            {t("auth.splitScreen.statEncryptionLabel")}
          </span>
          <span className="text-data-tabular text-secondary">AES-256</span>
        </div>
      </div>
    </div>
  );
}
